import React from "react";
import L from "leaflet";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import { C } from "../theme";
import CollapsibleSection from "./CollapsibleSection";

// AI-recommended fishing hot spots, derived from the angler's own catch GPS
// clusters (see functions-python pattern_facts.py — greedy 200m clusters,
// capped server-side at 5). Collapsed by default; the caller decides where it
// lives. Top-ranked spot is gold, the rest are pale-blue.

function rankColor(rank) {
  return rank === 1 ? C.gold : C.sand;
}

function buildHotspotIcon(rank) {
  const color = rankColor(rank);
  return L.divIcon({
    className: "",
    html:
      `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;` +
      `transform:rotate(-45deg);background:${color};border:2px solid #ffffff;` +
      `box-shadow:0 0 10px ${color}aa;display:flex;align-items:center;justify-content:center;">` +
      `<span style="transform:rotate(45deg);color:#05101f;font-size:10px;font-weight:800;">${rank}</span></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 22],
  });
}

// Fit the map to all markers once they're known. Re-runs if the spots change.
function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [28, 28], maxZoom: 15 });
  }, [map, points]);
  return null;
}

export default function HotspotMap({ hotspots = [], defaultOpen = false, locked = false, center = null, centerLabel = null }) {
  const [open, setOpen] = React.useState(defaultOpen);

  const spots = React.useMemo(
    () =>
      (Array.isArray(hotspots) ? hotspots : [])
        .filter((h) => Number.isFinite(h?.lat) && Number.isFinite(h?.lng))
        .slice(0, 5),
    [hotspots]
  );

  // Map center: prefer real hotspots, fall back to the caller-supplied
  // location (tide station for saltwater, device GPS for lake / stream).
  const mapCenter = React.useMemo(() => {
    if (spots.length > 0) return [spots[0].lat, spots[0].lng];
    if (center && Number.isFinite(center.lat) && Number.isFinite(center.lng)) {
      return [center.lat, center.lng];
    }
    return null;
  }, [spots, center]);

  const countLabel = locked
    ? null
    : spots.length > 0
    ? `· ${spots.length} spot${spots.length === 1 ? "" : "s"}`
    : "· none yet";

  return (
    <CollapsibleSection
      toggleTestId="dash-hotspot-toggle"
      open={open}
      onToggle={() => setOpen((o) => !o)}
      headerTitle={open ? "Hide recommended hot spots" : "Show recommended hot spots"}
      title={
        <span style={{ fontSize: 9, color: C.gold, letterSpacing: 1.5 }}>AI HOT SPOTS</span>
      }
      subtitle={countLabel || null}
    >
      <div>
          {spots.length === 0 ? (
            <>
              <div
                style={{
                  borderRadius: 10,
                  overflow: "hidden",
                  border: `1px solid ${C.border}`,
                  height: 180,
                  position: "relative",
                  background: "#06192e",
                }}
              >
                {mapCenter ? (
                  <MapContainer
                    center={mapCenter}
                    zoom={12}
                    zoomControl
                    attributionControl={false}
                    dragging
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  >
                    <TileLayer url="https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                  </MapContainer>
                ) : (
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg,#06192e,#0a2440)" }} />
                )}
                {/* No-AI-data watermark overlay */}
                <div
                  data-testid="dash-hotspot-empty-watermark"
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1200,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    padding: "0 18px",
                    pointerEvents: "none",
                    background: "rgba(4,12,24,0.42)",
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, color: "#ffffff", opacity: 0.9 }}>
                    NO AI HOT SPOTS YET
                  </div>
                  <div style={{ fontSize: 10, color: "#ffffff", opacity: 0.7, marginTop: 4, lineHeight: 1.4 }}>
                    {locked
                      ? "Pro plots your most productive spots here, ranked from your own catch history."
                      : "Once a handful of your catches cluster in one area, your top spots appear here."}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 9, color: C.muted, fontStyle: "italic", marginTop: 6 }}>
                {centerLabel ? `Showing ${centerLabel}. ` : ""}Imagery © Esri.
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  borderRadius: 10,
                  overflow: "hidden",
                  border: `1px solid ${C.border}`,
                  height: 180,
                  position: "relative",
                  background: "#06192e",
                }}
              >
                <MapContainer
                  center={[spots[0].lat, spots[0].lng]}
                  zoom={13}
                  zoomControl
                  attributionControl={false}
                  dragging
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                >
                  <TileLayer url="https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                  <FitBounds points={spots} />
                  {spots.map((h, i) => (
                    <Marker
                      key={h.id || `hs-${i}`}
                      position={[h.lat, h.lng]}
                      icon={buildHotspotIcon(i + 1)}
                    />
                  ))}
                </MapContainer>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
                {spots.map((h, i) => {
                  const species = Array.isArray(h.sampleSpecies)
                    ? h.sampleSpecies.filter(Boolean)
                    : [];
                  return (
                    <div
                      key={h.id || `hs-row-${i}`}
                      style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, lineHeight: 1.4 }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          flexShrink: 0,
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          background: rankColor(i + 1),
                          color: "#05101f",
                          fontSize: 9,
                          fontWeight: 800,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {i + 1}
                      </span>
                      <span style={{ color: C.sand, fontWeight: 600 }}>{h.label || `Spot ${i + 1}`}</span>
                      <span style={{ color: C.muted }}>
                        · {h.catchCount} catch{h.catchCount === 1 ? "" : "es"}
                        {species.length > 0 ? ` · ${species.join(", ")}` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div style={{ fontSize: 9, color: C.muted, fontStyle: "italic", marginTop: 6 }}>
                Based on where your past catches clustered. Imagery © Esri.
              </div>
            </>
          )}
      </div>
    </CollapsibleSection>
  );
}
